/**
 * Bankroll runs: the one definition of a paper bankroll "bust".
 *
 * A run starts with a fixed pool. When the strategy wants a bet (a full pool
 * would stake something) but the current bankroll sizes it to $0, the run
 * ends there: it is topped back up to the pool, what was left carries over
 * (so money paid in minus the current bankroll is always the lifetime loss),
 * and that bet opens the next run. Without this, a bankroll worn down to a
 * few dollars silently stops betting and its loss freezes at about the pool.
 *
 * Shared by the app's permanent bankroll ledger (src/lib/bankrollLedger.ts)
 * and the shadow-policy replays (paperStrategy/dollarPnl.ts), so a bust
 * means the same thing on every card. Pure; paper only.
 */
export function newBankrollRun(n, startedMs, startingPool) {
    return {
        n,
        startedMs,
        endedMs: null,
        start: startingPool,
        end: startingPool,
        peak: startingPool,
        low: startingPool,
        bets: 0,
        wins: 0,
    };
}
export function emptyBankrollRuns() {
    return { runs: [], busts: 0, deposited: 0 };
}
/** Opens run 1 with the first deposit, if no run exists yet. Mutates `state`. */
export function openFirstRun(state, startedMs, startingPool) {
    if (state.runs.length > 0)
        return;
    state.runs.push(newBankrollRun(1, startedMs, startingPool));
    state.deposited = startingPool;
}
/**
 * Sizes a bet the strategy wants against the current run, busting first if
 * the bankroll can't cover it. `size` gets the bankroll to size against and
 * whether that is a freshly opened run (so a policy that carries state
 * within a run, like a loss streak, can start it over). Call only for a bet
 * a full pool would place, after `openFirstRun`. Mutates `state`.
 */
export function stakeForBet(state, closeMs, startingPool, size, topUp = true) {
    let run = state.runs[state.runs.length - 1];
    const stake = size(run.end, run.bets === 0);
    if (stake > 0 || !topUp)
        return { stake: Math.max(0, stake), run, busted: false };
    run.endedMs = closeMs;
    state.busts++;
    state.deposited += startingPool - run.end;
    run = newBankrollRun(run.n + 1, closeMs, startingPool);
    state.runs.push(run);
    return { stake: size(run.end, true), run, busted: true };
}
/** Settles one bet into its run. Mutates `run`. */
export function recordRunBet(run, pnl, won) {
    run.bets++;
    if (won)
        run.wins++;
    run.end += pnl;
    run.peak = Math.max(run.peak, run.end);
    run.low = Math.min(run.low, run.end);
}
//# sourceMappingURL=bankrollRuns.js.map